import { FC, useState } from 'react'
import { Button, Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  buttonStyle,
  passwordStrengthTransitionStyle,
  passwordStrengthHiddenStyle
} from './ChangePasswordInputs.styles'
import { ChangePasswordFormData, ChangePasswordInputsProps } from './ChangePasswordInputs.types'
import { useChangePasswordValidations } from '../../../hooks/useChangePasswordValidations'
import PasswordStrength from '../PasswordStrength/PasswordStrength'

const ChangePasswordInputs: FC<ChangePasswordInputsProps> = ({
  onSubmit,
  loading = false,
  submitButtonText,
  validationConfig,
  disabled = false
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [currentPassword, setCurrentPassword] = useState('')
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false)
  const [hasStartedTyping, setHasStartedTyping] = useState(false)
  const { validateAllRules } = useChangePasswordValidations(validationConfig)

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCurrentPassword(value)
    if (value.length > 0 && !hasStartedTyping) {
      setHasStartedTyping(true)
    }
  }

  const handlePasswordFocus = () => {
    setIsPasswordInputFocused(true)
  }

  const handlePasswordBlur = () => {
    setIsPasswordInputFocused(false)
  }

  const handleSubmit = async (values: ChangePasswordFormData) => {
    if (!onSubmit || loading || disabled) return
    onSubmit(values)
  }

  const passwordRules = [
    {
      required: true,
      message: ''
    },
    {
      validator: (_: any, value: string) => {
        const error = validateAllRules(value)
        return error ? Promise.reject() : Promise.resolve()
      }
    }
  ]

  const confirmPasswordRules = [
    {
      required: true,
      message: ''
    },
    ({ getFieldValue }: any) => ({
      validator(_: any, value: string) {
        if (!value || getFieldValue('new_password') === value) {
          return Promise.resolve()
        }
        return Promise.reject(new Error(t('password_strength.password_not_match')))
      }
    })
  ]

  return (
    <div>
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Form.Item label={t('user-profile.second-table.new-pass')} name='new_password' rules={passwordRules}>
          <Input.Password
            placeholder={t('user-profile.second-table.enter-new-password')}
            disabled={disabled}
            onChange={handlePasswordChange}
            onFocus={handlePasswordFocus}
            onBlur={handlePasswordBlur}
          />
        </Form.Item>

        <div style={hasStartedTyping ? passwordStrengthTransitionStyle : passwordStrengthHiddenStyle}>
          <PasswordStrength
            password={currentPassword}
            showDetailedView={!isPasswordInputFocused}
            validationConfig={validationConfig}
          />
        </div>

        <Form.Item
          label={t('user-profile.second-table.confirm-pass')}
          name='confirm_new_password'
          dependencies={['new_password']}
          rules={confirmPasswordRules}
        >
          <Input.Password placeholder={t('user-profile.second-table.enter-confirm-password')} disabled={disabled} />
        </Form.Item>

        <Form.Item>
          <Button block style={buttonStyle} htmlType='submit' loading={loading} disabled={disabled}>
            {submitButtonText || t('user-profile.second-table.title')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}

export default ChangePasswordInputs
